import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { WebAPIClient } from './http-services/http-service';
import { TicketService } from './ticket.service';
import { PurchaseFormService } from './purchase-form.service';

export interface TransactionResult {
  orderReady: boolean;
}

export enum VerificationState {
  Verifying = 'verifying',
  Success = 'success',
  Failed = 'failed',
  Timeout = 'timeout'
}

export interface VerificationResult {
  state: VerificationState;
  orderId: string | null;
}

@Injectable({ providedIn: 'root' })
export class PaymentVerificationService {
  constructor(
    private http: WebAPIClient,
    private ticketService: TicketService,
    private formService: PurchaseFormService
  ) { }

  async verifyPayment(): Promise<VerificationResult> {
    const orderId = sessionStorage.getItem('pendingOrderId');

    if (!orderId) {
      return { state: VerificationState.Failed, orderId: null };
    }

    const delays = [2000, 5000, 5000, 5000];

    for (let i = 0; i < delays.length; i++) {
      await this.delay(delays[i]);

      const state = await this.attemptVerification(orderId);

      if (state === VerificationState.Success) {
        return { state: VerificationState.Success, orderId };
      }

      if (state === VerificationState.Failed) {
        return { state: VerificationState.Failed, orderId: null };
      }
    }

    return { state: VerificationState.Timeout, orderId: null };
  }

  async retryVerification(): Promise<VerificationResult> {
    const orderId = sessionStorage.getItem('pendingOrderId');

    if (!orderId) {
      return { state: VerificationState.Failed, orderId: null };
    }

    await this.delay(5000);
    const state = await this.attemptVerification(orderId);

    if (state === VerificationState.Success) {
      return { state: VerificationState.Success, orderId };
    }

    return { state: VerificationState.Timeout, orderId: null };
  }

  async cancelOrder(): Promise<void> {
    const orderId = sessionStorage.getItem('pendingOrderId');

    if (!orderId) return;

    try {
      await firstValueFrom(this.http.post(`orders/${orderId}/Cancel`, {}));
    } catch (error) {
    } finally {
      this.clearPendingOrder();
    }
  }

  private async attemptVerification(orderId: string): Promise<VerificationState> {
    try {
      const result = await firstValueFrom(this.checkTransaction(orderId));

      if (result?.orderReady === true) {
        await this.finalizeOrder();
        return VerificationState.Success;
      }

      return VerificationState.Verifying;
    } catch (error) {
      return VerificationState.Verifying;
    }
  }

  private checkTransaction(orderId: string): Observable<TransactionResult> {
    return this.http.post<TransactionResult>(`orders/${orderId}/CheckTransactionIssueTicket`, {}, { withCredentials: true });
  }

  private async finalizeOrder(): Promise<void> {
    const ticketData = await firstValueFrom(this.ticketService.ticketData$);
    this.formService.clearFormState();
    if (ticketData) {
      sessionStorage.removeItem(`ticketData_${ticketData.id}`);
    }
    this.clearPendingOrder();
  }

  private clearPendingOrder(): void {
    sessionStorage.removeItem('pendingOrderId');
    sessionStorage.removeItem('pendingSpecialTicketProductId');
    sessionStorage.removeItem('pendingOrderPayload');
    sessionStorage.removeItem('pendingPaymentRedirectUrl');
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
